import { createContext, useContext, useMemo, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import queryString from "query-string";

import api from "libs/api";

const InvoicesContext = createContext();

const InvoicesProvider = ({ children }) => {
  const { search } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const filters = queryString.parse(search);
  const { page, ...requestFilters } = filters;
  const default_sort = "created_at:desc";

  const [init_loading, setInitLoading] = useState(true);
  const [init_data_length, setInitDataLength] = useState(0);

  const { isLoading: loading, data: invoices, isSuccess } = useQuery({
    queryKey: ["invoices", requestFilters],
    queryFn: () =>
      api.get(
        `/user/invoices?${queryString.stringify({
          ...requestFilters,
          sort: filters?.sort || default_sort,
        })}`,
      ),
  });

  useEffect(() => {
    if (isSuccess && invoices) {
      setInitDataLength((prev) => (!!init_loading ? invoices?.length : prev));
      setInitLoading(false);
    }
  }, [isSuccess, invoices, init_loading]);

  const clearAllFilter = () => {
    const query = queryString.stringify({ sort: filters?.sort });
    navigate(`/invoices${query ? `?${query}` : ""}`);
  };

  const applyFilters = (values) => {
    navigate(
      `/invoices?${queryString.stringify({
        sort: filters?.sort,
        website: values.website || undefined,
        start_date: values.start_date || undefined,
        end_date: values.end_date || undefined,
      })}`,
    );
  };

  const filterHandler = (name, value) => {
    navigate(
      `/invoices?${queryString.stringify({
        ...filters,
        page: undefined,
        [name]: value || undefined,
      })}`,
    );
  };

  const showInvoice = async (invoiceOrId) => {
    try {
      const invoice =
        invoiceOrId && typeof invoiceOrId === "object" ? invoiceOrId : null;
      const invoice_id = invoice?.invoice_id || invoiceOrId;
      const fakturownia_invoice_url =
        invoice?.fakturownia_invoice_url ||
        invoices?.find((item) => item?.invoice_id === invoice_id)
          ?.fakturownia_invoice_url;

      if (fakturownia_invoice_url) {
        window.open(fakturownia_invoice_url, "_blank").focus();
        return;
      }

      const invoice_link = await api.get(`/user/invoices/${invoice_id}`);
      window.open(invoice_link, "_blank").focus();
    } catch (err) {
      console.error(err);
      toast.error(
        t("Unable to open the invoice. Please try again or contact us."),
      );
    }
  };

  const value = useMemo(() => {
    return {
      data: invoices,
      init_data_length,
      loading,
      init_loading,
      filters,
      filterHandler,
      clearAllFilter,
      applyFilters,
      showInvoice,
    };
    // eslint-disable-next-line
  }, [invoices, loading, init_loading, init_data_length, filters]);

  return (
    <InvoicesContext.Provider value={value}>
      {children}
    </InvoicesContext.Provider>
  );
};

const useInvoices = () => useContext(InvoicesContext);
export { InvoicesContext, useInvoices };
export default InvoicesProvider;
